import { Address as MyleModelsCommonResponseAddress } from "@/Common/Api/Contacts/Dto/Address";
import { ContactPoint } from "@/Common/Api/Contacts/Dto/ContactPoint";
import { Email } from "@/Common/Api/Contacts/Dto/Email";
import { PhoneNumber } from "@/Common/Api/Contacts/Dto/PhoneNumber";
import { LinkedContact } from "@/Common/Api/Patient/Dto/LinkedContact";
import { IInvitationContactEmail, IInvitationContactPhone } from "@/Common/Components/Portal/InvitationPopup/InvitationForm/ContactForm/AddContactForm/AddContactForm";
import { ArrayHelper } from "@/Common/Helpers/ArrayHelper";
import { GuidHelper } from "@/Common/Helpers/GuidHelper";
import { MyleHelper } from "@/Common/Helpers/MyleHelper";
import { ValidationHelper } from "@/Common/Helpers/ValidationHelper";
import { ContactPointType } from "@/Common/Models/Contacts/Enums/ContactPointType";
import { PhoneType } from "@/Common/Models/Contacts/Enums/PhoneType";
import { EditMode, ProfileTab } from "@/Common/Models/Patient/PatientEnums";
import { IPatientContactsModel } from "@/Common/Models/Patient/UI/Profile/IPatientContactsModel";
import * as LocalPatientOperations from "@/Common/Operations/operations.patient.local";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { BaseOperationHandler } from "@/Common/Stores/Handlers/Local/BaseOperationHandler";
import { PatientStore } from "@/Patient/Stores/PatientStore";

class PatientProfileContactsHandler extends BaseOperationHandler {
    constructor() {
        super();

        this.RegisterOperation(LocalPatientOperations.Profile.Contacts_CancelClick, this.contactsCancelClick);
        this.RegisterOperation(LocalPatientOperations.Profile.Contacts_SaveClick, this.contactsSaveClick);
        this.RegisterOperation(LocalPatientOperations.Profile.Contacts_EditClick, this.contactsEditClick);
        this.RegisterOperation(LocalPatientOperations.Profile.Contacts_AddAddress, this.contactsAddAddress);
        this.RegisterOperation(LocalPatientOperations.Profile.Contacts_RemoveAddress, this.contactsRemoveAddress);
        this.RegisterOperation(LocalPatientOperations.Profile.Contacts_AddPhone, this.contactsAddPhone);
        this.RegisterOperation(LocalPatientOperations.Profile.Contacts_RemovePhone, this.contactsRemovePhone);
        this.RegisterOperation(LocalPatientOperations.Profile.Contacts_AddEmail, this.contactsAddEmail);
        this.RegisterOperation(LocalPatientOperations.Profile.Contacts_RemoveEmail, this.contactsRemoveEmail);
        this.RegisterOperation(LocalPatientOperations.Profile.Contacts_AddressTypeSelect, this.contactsAddressTypeChange);
        this.RegisterOperation(LocalPatientOperations.Profile.Contacts_AddressChange, this.contactsAddressChange);
        this.RegisterOperation(LocalPatientOperations.Profile.Contacts_AddressCityChange, this.contactsAddressCityChange);
        this.RegisterOperation(LocalPatientOperations.Profile.Contacts_AddressCountrySelect, this.contactsAddressCountrySelect);
        this.RegisterOperation(LocalPatientOperations.Profile.Contacts_AddressCountryRegionSelect, this.contactsAddressCountryRegionSelect);
        this.RegisterOperation(LocalPatientOperations.Profile.Contacts_AddressZipCodeChange, this.contactsAddressZipCodeChange);
        this.RegisterOperation(LocalPatientOperations.Profile.Contacts_AddressIsPreferredCheck, this.contactsAddressIsPreferredChange);
        this.RegisterOperation(LocalPatientOperations.Profile.Contacts_PhoneTypeSelect, this.contactsPhoneTypeSelect);
        this.RegisterOperation(LocalPatientOperations.Profile.Contacts_PhoneCountrySelect, this.contactsPhoneCountrySelect);
        this.RegisterOperation(LocalPatientOperations.Profile.Contacts_PhoneNumberChange, this.contactsPhoneNumberChange);
        this.RegisterOperation(LocalPatientOperations.Profile.Contacts_PhoneExtChange, this.contactsPhoneExtNumberChange);
        this.RegisterOperation(LocalPatientOperations.Profile.Contacts_PhoneNotesChange, this.contactsPhoneNotesChange);
        this.RegisterOperation(LocalPatientOperations.Profile.Contacts_PhoneIsPreferredCheck, this.contactsPhoneIsPreferredChange);
        this.RegisterOperation(LocalPatientOperations.Profile.Contacts_PhoneDefaultNotificationChange, this.contactsPhoneDefaultNotificationChange);
        this.RegisterOperation(LocalPatientOperations.Profile.Contacts_EmailTypeSelect, this.contactsEmailTypeSelect);
        this.RegisterOperation(LocalPatientOperations.Profile.Contacts_EmailAddressChange, this.contactsEmailAddressChange);
        this.RegisterOperation(LocalPatientOperations.Profile.Contacts_EmailDefaultNotificationChange, this.contactsEmailDefaultNotificationChange);
        this.RegisterOperation(LocalPatientOperations.Profile.Contacts_EmailCanContactChange, this.contactsEmailCanContactChange);
        this.RegisterOperation(LocalPatientOperations.Profile.Contacts_DoNotEmailChange, this.contactsDoNotEmailChange);
        this.RegisterOperation(LocalPatientOperations.Profile.Contacts_EditEmailsClick, this.contactsEditEmailsClick);
        this.RegisterOperation(LocalPatientOperations.Profile.Contacts_CloseEmailsClick, this.contactsCloseEmailsClick);
        this.RegisterOperation(LocalPatientOperations.Profile.Contacts_UpdateNotificationsFromAppointmentSave, this.updatePatientProfileNotificationsFromAppointmentSave);
    }

    private contactsDoNotEmailChange(request: LocalEvent, store: PatientStore): void {
        const doNotEmail = request.Get<boolean>("checked");

        let contactsModel = store.PatientProfileContactsModels[request.PatientId];

        if (doNotEmail) {
            contactsModel = contactsModel.set("Emails", Immutable.List([]));
        }
        else {
            contactsModel = this.addEmptyEmailToModel(contactsModel.set("Emails", Immutable.List([])));
        }

        contactsModel = contactsModel.set("DoNotEmail", doNotEmail);

        store.PatientProfileContactsModels[request.PatientId] = contactsModel;
    }

    public contactsEditClick(request: LocalEvent, store: PatientStore): void {
        let uiModel = store.PatientUIModels[request.PatientId];

        let modelToUndo: IPatientContactsModel = store.PatientProfileContactsModels[request.PatientId];
        uiModel = uiModel.set("PatientProfileEditMode", EditMode.Edit);

        // cloning for the deep-nested immutable lists
        let phoneClones = MyleHelper.CloneObject<PhoneNumber[]>(modelToUndo.get("PhoneNumbers").toArray());
        let addressesClones = MyleHelper.CloneObject<MyleModelsCommonResponseAddress[]>(modelToUndo.get("Addresses").toArray());
        let emailClones = MyleHelper.CloneObject<Email[]>(modelToUndo.get("Emails").toArray());
        let representatives = MyleHelper.CloneObject<LinkedContact[]>(modelToUndo.get("Representatives")?.toArray() ?? []);
        let emergencyContacts = MyleHelper.CloneObject<LinkedContact[]>(modelToUndo.get("EmergencyContacts")?.toArray() ?? []);

        modelToUndo = modelToUndo
            .set("PhoneNumbers", Immutable.List(phoneClones))
            .set("Addresses", Immutable.List(addressesClones))
            .set("Emails", Immutable.List(emailClones))
            .set("Representatives", Immutable.List(representatives))
            .set("EmergencyContacts", Immutable.List(emergencyContacts));

        let contactModel = store.PatientProfileContactsModels[request.PatientId];

        // set default empty fields for edition
        if (contactModel.get("Addresses").count() == 0) {
            contactModel = this.addEmptyAddressToModel(contactModel);
        }
        if (contactModel.get("Emails").count() == 0 && (cacheStore.ClinicSettings.EnablePatientEdit || !cacheStore.ClinicSettings.EnablePatientEditEmail)) {
            contactModel = this.addEmptyEmailToModel(contactModel);
        }
        if (contactModel.get("PhoneNumbers").count() == 0) {
            contactModel = this.addEmptyPhoneToModel(contactModel);
        }

        store.PatientUIModels[request.PatientId] = uiModel;
        store.PatientProfileContactsUndoModels[request.PatientId] = modelToUndo;
        store.PatientProfileContactsModels[request.PatientId] = contactModel;
    }

    private contactsEditEmailsClick(request: LocalEvent, store: PatientStore): void {
        let contactsModel = store.PatientProfileContactsModels[request.PatientId];

        contactsModel = contactsModel.set("ShowChangeEmailPopup", true);

        store.PatientProfileContactsModels[request.PatientId] = contactsModel;
    }

    private contactsCloseEmailsClick(request: LocalEvent, store: PatientStore): void {
        let patientContactModel = store.PatientProfileContactsModels[request.PatientId];
        patientContactModel = patientContactModel.set("ShowChangeEmailPopup", false);

        store.PatientProfileContactsModels[request.PatientId] = patientContactModel;

        let uiModel = store.PatientUIModels[request.PatientId];
        uiModel = uiModel.set("PatientProfileActiveTab", ProfileTab.Contacts);
        uiModel = uiModel.set("PatientProfileEditMode", EditMode.View);

        store.PatientUIModels[request.PatientId] = uiModel;
    }

    private updatePatientProfileNotificationsFromAppointmentSave(request: LocalEvent, store: PatientStore): void {
        const patientContactId = request.Get<string>("patientContactId");
        const notificationsContactPointIds = request.Get<string[]>("notificationsContactPointIds");
        let patient = store.PatientProfileContactsModels[patientContactId];

        if (patient) {
            patient = patient
                .set("ContactPoints", patient.get("ContactPoints").mutate(s => s.NotificationsEnabled = notificationsContactPointIds.contains(s.Id)))
                .set("Emails", patient.get("Emails").mutate(s => s.NotificationsEnabled = notificationsContactPointIds.contains(s.ContactPoint_id)))
                .set("PhoneNumbers", patient.get("PhoneNumbers").mutate(s => s.NotificationsEnabled = notificationsContactPointIds.contains(s.ContactPoint_id)));

            const representatives = patient.get("Representatives").mutate(s => {
                s.Emails = s.Emails.mutate(p => p.NotificationsEnabled = notificationsContactPointIds.contains(p.ContactPoint_id));
                s.PhoneNumbers = s.PhoneNumbers.mutate(p => p.NotificationsEnabled = notificationsContactPointIds.contains(p.ContactPoint_id));
            });

            store.PatientProfileContactsModels[patientContactId] = patient.set("Representatives", representatives);
        }
    }

    public contactsCancelClick(request: LocalEvent, store: PatientStore): void {
        var uiModel = store.PatientUIModels[request.PatientId];
        var undoModel = store.PatientProfileContactsUndoModels[request.PatientId];
        uiModel = uiModel.set("PatientProfileEditMode", EditMode.View);

        store.PatientProfileContactsModels[request.PatientId] = undoModel;
        store.PatientUIModels[request.PatientId] = uiModel;
        delete store.PatientProfileContactsUndoModels[request.PatientId];
    }

    private contactsSaveClick(request: LocalEvent, store: PatientStore): void {
        let contactsModel = store.PatientProfileContactsModels[request.PatientId];
        let currentContactPoints = contactsModel.get("ContactPoints").toArray();

        // check for empty addresses - don't save them
        const addressesToKeep: MyleModelsCommonResponseAddress[] = [];
        contactsModel.get("Addresses").toArray().forEach(x => {
            if ((x.StreetAddress?.trim().length > 0) || (x.City?.trim().length > 0) || (x.ZipCode?.trim().length > 0))
                addressesToKeep.push(x);
            else
                currentContactPoints = currentContactPoints.filter(c => c.Id != x.ContactPoint_id);
        });
        contactsModel = contactsModel.set("Addresses", Immutable.List(addressesToKeep));

        // check for empty phones - don't save them
        const phonesToKeep: PhoneNumber[] = [];
        contactsModel.get("PhoneNumbers").toArray().forEach(x => {
            if ((x.Number?.trim().length > 0) || (x.Notes?.trim().length > 0))
                phonesToKeep.push(x);
            else
                currentContactPoints = currentContactPoints.filter(c => c.Id != x.ContactPoint_id);
        });
        contactsModel = contactsModel.set("PhoneNumbers", Immutable.List(phonesToKeep));

        // check for empty email - don't save them
        const emailsToKeep: Email[] = [];
        contactsModel.get("Emails").toArray().forEach(x => {
            if ((x.Address?.trim().length > 0))
                emailsToKeep.push(x);
            else
                currentContactPoints = currentContactPoints.filter(c => c.Id != x.ContactPoint_id);
        });
        contactsModel = contactsModel.set("Emails", Immutable.List(emailsToKeep));

        contactsModel = contactsModel
            .set("ContactPoints", Immutable.List(currentContactPoints))
            .set("DoNotEmail", contactsModel.get("DoNotEmail"));

        store.PatientProfileContactsModels[request.PatientId] = contactsModel;

        store.ActionsProfile.savePatientContacts(contactsModel);
    }

    private contactsAddAddress(request: LocalEvent, store: PatientStore): void {
        var contactsModel = store.PatientProfileContactsModels[request.PatientId];
        contactsModel = this.addEmptyAddressToModel(contactsModel);
        store.PatientProfileContactsModels[request.PatientId] = contactsModel;
    }

    private contactsRemoveAddress(request: LocalEvent, store: PatientStore): void {
        var contactsModel = store.PatientProfileContactsModels[request.PatientId];
        var currentAddresses = contactsModel.get("Addresses").toArray();
        var currentContactPoints = contactsModel.get("ContactPoints").toArray();
        var idToremove = request.Get<string>("value");

        currentAddresses = currentAddresses.filter(x => x.ContactPoint_id !== idToremove);
        currentContactPoints = currentContactPoints.filter(x => x.Id !== idToremove);
        contactsModel = contactsModel.set("ContactPoints", Immutable.List(currentContactPoints));
        contactsModel = contactsModel.set("Addresses", Immutable.List(currentAddresses));
        store.PatientProfileContactsModels[request.PatientId] = contactsModel;

        if (contactsModel.get("Addresses").count() == 0) {
            contactsModel = this.addEmptyAddressToModel(contactsModel);
        }

        store.PatientProfileContactsModels[request.PatientId] = contactsModel;
    }

    private contactsAddPhone(request: LocalEvent, store: PatientStore): void {
        const phoneContact = request.Get<IInvitationContactPhone>("value");

        let contactsModel = store.PatientProfileContactsModels[request.PatientId];

        contactsModel = phoneContact
            ? this.addPhoneToModel(contactsModel, phoneContact)
            : this.addEmptyPhoneToModel(contactsModel);

        store.PatientProfileContactsModels[request.PatientId] = contactsModel;
    }

    private contactsRemovePhone(request: LocalEvent, store: PatientStore): void {
        var contactsModel = store.PatientProfileContactsModels[request.PatientId];
        var currentPhones = contactsModel.get("PhoneNumbers").toArray();
        var currentContactPoints = contactsModel.get("ContactPoints").toArray();

        var idToremove = request.Get<string>("value");

        currentPhones = currentPhones.filter(x => x.ContactPoint_id !== idToremove);
        currentContactPoints = currentContactPoints.filter(x => x.Id !== idToremove);
        contactsModel = contactsModel.set("PhoneNumbers", Immutable.List(currentPhones));
        contactsModel = contactsModel.set("ContactPoints", Immutable.List(currentContactPoints));
        store.PatientProfileContactsModels[request.PatientId] = contactsModel;

        if (contactsModel.get("PhoneNumbers").count() == 0) {
            contactsModel = this.addEmptyPhoneToModel(contactsModel);
        }

        store.PatientProfileContactsModels[request.PatientId] = contactsModel;
    }

    private contactsAddEmail(request: LocalEvent, store: PatientStore): void {
        const emailContact = request.Get<IInvitationContactEmail>("value");

        let contactsModel = store.PatientProfileContactsModels[request.PatientId];

        contactsModel = emailContact
            ? this.addEmailToModel(contactsModel, emailContact)
            : this.addEmptyEmailToModel(contactsModel);

        store.PatientProfileContactsModels[request.PatientId] = contactsModel;
    }

    private contactsRemoveEmail(request: LocalEvent, store: PatientStore): void {
        var contactsModel = store.PatientProfileContactsModels[request.PatientId];
        var currentEmails = contactsModel.get("Emails").toArray();
        var currentContactPoints = contactsModel.get("ContactPoints").toArray();

        var idToremove = request.Get<string>("value");
        currentEmails = currentEmails.filter(x => x.ContactPoint_id !== idToremove);
        currentContactPoints = currentContactPoints.filter(x => x.Id !== idToremove);
        contactsModel = contactsModel.set("Emails", Immutable.List(currentEmails));
        contactsModel = contactsModel.set("ContactPoints", Immutable.List(currentContactPoints));
        store.PatientProfileContactsModels[request.PatientId] = contactsModel;

        if (contactsModel.get("Emails").count() == 0) {
            contactsModel = this.addEmptyEmailToModel(contactsModel);
        }

        store.PatientProfileContactsModels[request.PatientId] = contactsModel;
    }

    private contactsAddressTypeChange(request: LocalEvent, store: PatientStore): void {
        var selectedTypeLookupId = request.Get<string>("value");
        var address = request.Get<MyleModelsCommonResponseAddress>("item");

        var contactsModel = store.PatientProfileContactsModels[request.PatientId];
        var currentAddresses: MyleModelsCommonResponseAddress[] = contactsModel.get("Addresses").toArray();
        var currentContactPoints: ContactPoint[] = contactsModel.get("ContactPoints").toArray();

        for (var i = 0; i < currentAddresses.length; i++) {
            var x = currentAddresses[i];
            if (x.ContactPoint_id === address.ContactPoint_id) {
                var clone: MyleModelsCommonResponseAddress = MyleHelper.CloneObject(x);
                clone.ContactPointTypeLookupId = selectedTypeLookupId;
                currentAddresses[i] = clone;

                var contactPoint = currentContactPoints.filter(x => x.Id == clone.ContactPoint_id);
                if (contactPoint && contactPoint.length > 0) {
                    var cp = contactPoint[0];
                    cp.ContactPointTypeLookupId = selectedTypeLookupId;
                }
            }
        }

        contactsModel = contactsModel.set("Addresses", Immutable.List(currentAddresses));
        store.PatientProfileContactsModels[request.PatientId] = contactsModel;
    }

    private contactsAddressChange(request: LocalEvent, store: PatientStore): void {
        const newAddress = request.Get<string>("value");
        const address = request.Get<MyleModelsCommonResponseAddress>("item");

        let contactsModel = store.PatientProfileContactsModels[request.PatientId];
        const currentAddresses: MyleModelsCommonResponseAddress[] = contactsModel.get("Addresses").toArray();

        for (let i = 0; i < currentAddresses.length; i++) {
            const currentAddress = currentAddresses[i];
            if (currentAddress.ContactPoint_id === address.ContactPoint_id) {
                const clone = new MyleModelsCommonResponseAddress(currentAddress);
                clone.StreetAddress = newAddress;
                currentAddresses[i] = clone;
            }
        }

        contactsModel = contactsModel.set("Addresses", Immutable.List(currentAddresses));
        store.PatientProfileContactsModels[request.PatientId] = contactsModel;
    }

    private contactsAddressCityChange(request: LocalEvent, store: PatientStore): void {
        const val = request.Get<string>("value");
        const address = request.Get<MyleModelsCommonResponseAddress>("item");
        let contactsModel = store.PatientProfileContactsModels[request.PatientId];
        const currentAddresses: MyleModelsCommonResponseAddress[] = contactsModel.get("Addresses").toArray();

        for (let i = 0; i < currentAddresses.length; i++) {
            const currentAddress = currentAddresses[i];

            if (currentAddress.ContactPoint_id === address.ContactPoint_id) {
                const clone = new MyleModelsCommonResponseAddress(currentAddress);
                clone.City = val;
                currentAddresses[i] = clone;
            }
        }

        contactsModel = contactsModel.set("Addresses", Immutable.List(currentAddresses));
        store.PatientProfileContactsModels[request.PatientId] = contactsModel;
    }

    private contactsAddressCountrySelect(request: LocalEvent, store: PatientStore): void {
        const selectedCountryId = request.Get<string>("value");
        const selectedCountry = cacheStore.GetLookupById(selectedCountryId);
        const availableCountryRegionLookupIds = cacheStore.GetCountryRegionLookups(selectedCountry);
        const item = request.Get<MyleModelsCommonResponseAddress>("item");

        let contactsModel = store.PatientProfileContactsModels[request.PatientId];

        let currentAddresses = contactsModel
            .get("Addresses")
            .toArray()
            .map(address => {
                if (address.ContactPoint_id !== item.ContactPoint_id)
                    return address;

                const clone = new MyleModelsCommonResponseAddress(address);

                clone.CountryLookupId = selectedCountry.LookupID;
                clone.StateLookupId = availableCountryRegionLookupIds?.[0]?.LookupID ?? clone.CountryLookupId;

                return clone;
            });

        contactsModel = contactsModel.set("Addresses", Immutable.List(currentAddresses));
        store.PatientProfileContactsModels[request.PatientId] = contactsModel;
    }

    private contactsAddressCountryRegionSelect(request: LocalEvent, store: PatientStore): void {
        const val = request.Get<string>("value");
        const item = request.Get<MyleModelsCommonResponseAddress>("item");

        let contactsModel = store.PatientProfileContactsModels[request.PatientId];
        const currentAddresses = contactsModel
            .get("Addresses")
            .toArray()
            .map(address => {
                if (address.ContactPoint_id !== item.ContactPoint_id)
                    return address;

                const clone = new MyleModelsCommonResponseAddress(address);
                clone.StateLookupId = val;

                return clone;
            });

        contactsModel = contactsModel.set("Addresses", Immutable.List(currentAddresses));
        store.PatientProfileContactsModels[request.PatientId] = contactsModel;
    }

    private contactsAddressZipCodeChange(request: LocalEvent, store: PatientStore): void {
        const val = request.Get<string>("value");
        const item = request.Get<MyleModelsCommonResponseAddress>("item");
        let contactsModel = store.PatientProfileContactsModels[request.PatientId];

        // fetch postalcode validation info
        const validationHelper = new ValidationHelper();
        const validationRule = validationHelper.getPostalCodeValidationRule(item.CountryLookupId, null); // no need for region when checking for postal code format?
        const validationRegex: string = validationRule?.ValidationRegexPattern ?? "";

        const addresses = contactsModel
            .get("Addresses")
            .toArray()
            .map(address => {
                if (address.ContactPoint_id !== item.ContactPoint_id)
                    return address;

                const clone = new MyleModelsCommonResponseAddress(address);

                // set val
                clone.ZipCode = val;

                // check if valid with regex
                if (validationRegex?.length > 0) {
                    const regexp = new RegExp(validationRegex, "i");
                    const valid = val?.trim()?.length ? ValidationHelper.isExactMatch(val, regexp) : true;
                    clone.IsValid = valid;
                }

                return clone;
            });

        contactsModel = contactsModel.set("Addresses", Immutable.List(addresses));
        store.PatientProfileContactsModels[request.PatientId] = contactsModel;
    }

    private contactsAddressIsPreferredChange(request: LocalEvent, store: PatientStore): void {
        const isPreferred = request.Get<boolean>("value");
        const item = request.Get<MyleModelsCommonResponseAddress>("item");

        let contactsModel = store.PatientProfileContactsModels[request.PatientId];

        const currentContactPoints = contactsModel.get("ContactPoints").toArray();

        const currentAddresses = contactsModel
            .get("Addresses")
            .toArray()
            .map(address => {
                let clone: MyleModelsCommonResponseAddress = MyleHelper.CloneObject(address);
                clone.IsPreferred = clone.ContactPoint_id === item.ContactPoint_id && isPreferred;

                const currentContactPoint = currentContactPoints.firstOrDefault(cp => cp.Id == clone.ContactPoint_id);
                if (currentContactPoint) {
                    const idx = currentContactPoints.indexOf(currentContactPoint);
                    let cpclone: ContactPoint = MyleHelper.CloneObject(currentContactPoint);

                    if (currentContactPoint.Id == item.ContactPoint_id)
                        cpclone.IsPreferred = isPreferred;
                    else
                        cpclone.IsPreferred = false;

                    currentContactPoints[idx] = cpclone;
                }

                return clone;
            });

        contactsModel = contactsModel
            .set("Addresses", Immutable.List(currentAddresses))
            .set("ContactPoints", Immutable.List(currentContactPoints));

        store.PatientProfileContactsModels[request.PatientId] = contactsModel;
    }

    private contactsPhoneTypeSelect(request: LocalEvent, store: PatientStore): void {
        const value = request.Get<string>("value");

        const phone = request.Get<PhoneNumber>("item");
        const contactsModel = store.PatientProfileContactsModels[request.PatientId];
        const currentPhones = contactsModel.get("PhoneNumbers").toArray();
        const currentContactPoints = contactsModel.get("ContactPoints").toArray();
        const contactPointLookupId = value.split("_")[0];
        const phoneLookupId = value.split("_")[1];

        let workTypeId = cacheStore.FindLookupByLookupCode("WORK").LookupID;
        let phoneTypeId = cacheStore.FindLookupByLookupCode("PHONE").LookupID;
        let mobileTypeId = cacheStore.FindLookupByLookupCode("MOBILE").LookupID;

        for (var i = 0; i < currentPhones.length; i++) {
            const currentPhone = currentPhones[i];
            if (currentPhone.ContactPoint_id === phone.ContactPoint_id) {
                const clone: PhoneNumber = MyleHelper.CloneObject(currentPhone);
                clone.PhoneTypeLookupId = phoneLookupId;
                clone.ContactPointTypeLookupId = contactPointLookupId;

                if (clone.PhoneTypeLookupId !== mobileTypeId)
                    clone.NotificationsEnabled = false;

                const hasExtensionField = (clone.ContactPointTypeLookupId === workTypeId) && (phoneTypeId === clone.PhoneTypeLookupId);

                if (!hasExtensionField)
                    clone.Extension = null;
                currentPhones[i] = clone;

                const contactPoint = currentContactPoints.firstOrDefault(p => p.Id == clone.ContactPoint_id);
                if (contactPoint)
                    contactPoint.ContactPointTypeLookupId = contactPointLookupId;
            }
        }

        store.PatientProfileContactsModels[request.PatientId] = contactsModel
            .set("ContactPoints", Immutable.List(currentContactPoints))
            .set("PhoneNumbers", Immutable.List(currentPhones));
    }

    private contactsPhoneCountrySelect(request: LocalEvent, store: PatientStore): void {
        var val = request.Get<string>("value");
        var address = request.Get<PhoneNumber>("item");

        var contactsModel = store.PatientProfileContactsModels[request.PatientId];
        var currentPhones: PhoneNumber[] = contactsModel.get("PhoneNumbers").toArray();

        for (var i = 0; i < currentPhones.length; i++) {
            var x = currentPhones[i];
            if (x.ContactPoint_id === address.ContactPoint_id) {
                var clone: PhoneNumber = MyleHelper.CloneObject(x);
                clone.CountryLookupId = val;
                currentPhones[i] = clone;
            }
        }

        contactsModel = contactsModel.set("PhoneNumbers", Immutable.List(currentPhones));
        store.PatientProfileContactsModels[request.PatientId] = contactsModel;
    }

    private contactsPhoneDefaultNotificationChange(request: LocalEvent, store: PatientStore): void {
        const val = request.Get<boolean>("value");
        const phoneNumber = request.Get<PhoneNumber>("item");

        let contactsModel = store.PatientProfileContactsModels[request.PatientId];
        const currentPhones = contactsModel
            .get("PhoneNumbers")
            .map(currentPhone => {
                if (currentPhone.ContactPoint_id !== phoneNumber.ContactPoint_id)
                    return currentPhone;

                const clone = MyleHelper.CloneObject(currentPhone);
                clone.NotificationsEnabled = val;
                return clone;
            })
            .toArray();

        let contactPoints = contactsModel.get("ContactPoints")
            .mutate(item => item.NotificationsEnabled = val, item => item.Id === phoneNumber.ContactPoint_id);

        store.PatientProfileContactsModels[request.PatientId] = contactsModel
            .set("ContactPoints", contactPoints)
            .set("PhoneNumbers", Immutable.List(currentPhones));
    }

    private contactsPhoneNumberChange(request: LocalEvent, store: PatientStore): void {
        var val = request.Get<string>("value");
        var address = request.Get<PhoneNumber>("item");

        var contactsModel = store.PatientProfileContactsModels[request.PatientId];
        var currentPhones: PhoneNumber[] = contactsModel.get("PhoneNumbers").toArray();

        for (var i = 0; i < currentPhones.length; i++) {
            var x = currentPhones[i];
            if (x.ContactPoint_id === address.ContactPoint_id) {
                var clone: PhoneNumber = MyleHelper.CloneObject(x);
                clone.Number = val;

                clone = ValidationHelper.validatePhoneNumber(clone);

                currentPhones[i] = clone;
            }
        }

        contactsModel = contactsModel.set("PhoneNumbers", Immutable.List(currentPhones));
        store.PatientProfileContactsModels[request.PatientId] = contactsModel;
    }

    private contactsPhoneExtNumberChange(request: LocalEvent, store: PatientStore): void {
        var val = request.Get<string>("value");
        var address = request.Get<PhoneNumber>("item");

        var contactsModel = store.PatientProfileContactsModels[request.PatientId];
        var currentPhones: PhoneNumber[] = contactsModel.get("PhoneNumbers").toArray();

        for (var i = 0; i < currentPhones.length; i++) {
            var x = currentPhones[i];
            if (x.ContactPoint_id === address.ContactPoint_id) {
                var clone: PhoneNumber = MyleHelper.CloneObject(x);
                clone.Extension = val;
                clone = ValidationHelper.validatePhoneNumber(clone);
                currentPhones[i] = clone;
            }
        }

        contactsModel = contactsModel.set("PhoneNumbers", Immutable.List(currentPhones));
        store.PatientProfileContactsModels[request.PatientId] = contactsModel;
    }

    private contactsPhoneNotesChange(request: LocalEvent, store: PatientStore): void {
        var val = request.Get<string>("value");
        var address = request.Get<PhoneNumber>("item");

        var contactsModel = store.PatientProfileContactsModels[request.PatientId];
        var currentPhones: PhoneNumber[] = contactsModel.get("PhoneNumbers").toArray();

        for (var i = 0; i < currentPhones.length; i++) {
            var x = currentPhones[i];
            if (x.ContactPoint_id === address.ContactPoint_id) {
                var clone: PhoneNumber = MyleHelper.CloneObject(x);
                clone.Notes = val;
                currentPhones[i] = clone;
            }
        }

        contactsModel = contactsModel.set("PhoneNumbers", Immutable.List(currentPhones));
        store.PatientProfileContactsModels[request.PatientId] = contactsModel;
    }

    private contactsPhoneIsPreferredChange(request: LocalEvent, store: PatientStore): void {
        const value = request.Get<boolean>("value");
        const phone = request.Get<PhoneNumber>("item");

        const contactsModel = store.PatientProfileContactsModels[request.PatientId];
        const currentPhones = contactsModel.get("PhoneNumbers").toArray();
        const currentContactPoints = contactsModel.get("ContactPoints").toArray();

        for (let i = 0; i < currentPhones.length; i++) {
            const currentPhone = currentPhones[i];

            if (currentPhone.ContactPoint_id === phone.ContactPoint_id) {
                let clone = MyleHelper.CloneObject<PhoneNumber>(currentPhone);
                clone.IsPreferred = value;
                currentPhones[i] = clone;
            }
            else {
                let clone = MyleHelper.CloneObject<PhoneNumber>(currentPhone);
                clone.IsPreferred = false;
                currentPhones[i] = clone;
            }

            const currentContactPoint = ArrayHelper.Find(currentContactPoints, cp => cp.Id == currentPhone.ContactPoint_id);
            if (currentContactPoint) {
                const idx = currentContactPoints.indexOf(currentContactPoint);
                let cpclone = MyleHelper.CloneObject<ContactPoint>(currentContactPoint);

                if (currentContactPoint.Id == phone.ContactPoint_id)
                    cpclone.IsPreferred = value;
                else
                    cpclone.IsPreferred = false;

                currentContactPoints[idx] = cpclone;
            }
        }

        store.PatientProfileContactsModels[request.PatientId] = contactsModel
            .set("PhoneNumbers", Immutable.List(currentPhones))
            .set("ContactPoints", Immutable.List(currentContactPoints));
    }

    private contactsEmailTypeSelect(request: LocalEvent, store: PatientStore): void {
        var val = request.Get<string>("value");
        var address = request.Get<Email>("item");

        var contactsModel = store.PatientProfileContactsModels[request.PatientId];
        var currentEmails: Email[] = contactsModel.get("Emails").toArray();
        var currentContactPoints: ContactPoint[] = contactsModel.get("ContactPoints").toArray();

        for (var i = 0; i < currentEmails.length; i++) {
            let x = currentEmails[i];
            if (x.ContactPoint_id == address.ContactPoint_id) {
                let clone: Email = MyleHelper.CloneObject(x);
                clone.ContactPointTypeLookupId = val;
                currentEmails[i] = clone;
            }
        }
        for (var i = 0; i < currentContactPoints.length; i++) {
            let x = currentContactPoints[i];
            if (x.Id == address.ContactPoint_id) {
                let clone: ContactPoint = MyleHelper.CloneObject(x);
                clone.ContactPointTypeLookupId = val;
                currentContactPoints[i] = clone;
            }
        }

        contactsModel = contactsModel.set("Emails", Immutable.List(currentEmails));
        contactsModel = contactsModel.set("ContactPoints", Immutable.List(currentContactPoints));

        store.PatientProfileContactsModels[request.PatientId] = contactsModel;
    }

    private contactsEmailAddressChange(request: LocalEvent, store: PatientStore): void {
        var val = request.Get<string>("value");
        var address = request.Get<Email>("item");

        var contactsModel = store.PatientProfileContactsModels[request.PatientId];
        var currentEmails: Email[] = contactsModel.get("Emails").toArray();

        for (var i = 0; i < currentEmails.length; i++) {
            var x = currentEmails[i];
            if (x.ContactPoint_id === address.ContactPoint_id) {
                var clone: Email = MyleHelper.CloneObject(x);

                clone.Address = val;
                clone.IsValid = ValidationHelper.isExactMatch(clone.Address, ValidationHelper.emailRegex);

                currentEmails[i] = clone;
            }
        }

        contactsModel = contactsModel.set("Emails", Immutable.List(currentEmails));
        store.PatientProfileContactsModels[request.PatientId] = contactsModel;
    }

    private contactsEmailDefaultNotificationChange(request: LocalEvent, store: PatientStore): void {
        const val = request.Get<boolean>("checked");
        const email = request.Get<Email>("item");

        let contactsModel = store.PatientProfileContactsModels[request.PatientId];
        let currentEmails = contactsModel
            .get("Emails")
            .map(currentEmail => {
                if (currentEmail.ContactPoint_id !== email.ContactPoint_id)
                    return currentEmail;

                const clone = MyleHelper.CloneObject(currentEmail);
                clone.NotificationsEnabled = val;
                return clone;
            })
            .toArray();

        let contactPoints = contactsModel.get("ContactPoints")
            .mutate(item => item.NotificationsEnabled = val, item => item.Id === email.ContactPoint_id);

        store.PatientProfileContactsModels[request.PatientId] = contactsModel
            .set("ContactPoints", contactPoints)
            .set("Emails", Immutable.List(currentEmails));
    }

    private contactsEmailCanContactChange(request: LocalEvent, store: PatientStore): void {
        var address = request.Get<Email>("item");

        var contactsModel = store.PatientProfileContactsModels[request.PatientId];
        var currentEmails: Email[] = contactsModel.get("Emails").toArray();

        for (var i = 0; i < currentEmails.length; i++) {
            var x = currentEmails[i];
            if (x.ContactPoint_id === address.ContactPoint_id) {
                var clone: Email = MyleHelper.CloneObject(x);
                clone.IsOkToContact = !x.IsOkToContact;
                currentEmails[i] = clone;
            }
        }

        contactsModel = contactsModel.set("Emails", Immutable.List(currentEmails));
        store.PatientProfileContactsModels[request.PatientId] = contactsModel;
    }

    private addEmptyAddressToModel(contactsModel: IPatientContactsModel): IPatientContactsModel {
        var currentAddresses = contactsModel.get("Addresses").toArray();
        var currentContactPoints = contactsModel.get("ContactPoints").toArray();
        var address = new MyleModelsCommonResponseAddress();
        var contactPoint = new ContactPoint();
        contactPoint.Id = GuidHelper.NewGuid();
        contactPoint.ContactId = contactsModel.get("PatientContactId");
        contactPoint.ContactPointTypeLookupId = cacheStore.GetContactPointTypeLookups()[0].LookupID;

        currentContactPoints.push(contactPoint);

        address.IsValid = false;
        address.ContactPoint_id = contactPoint.Id;
        address.AvailableContactPointTypeLookupIds = cacheStore.GetContactPointTypeLookups();
        address.ContactPointTypeLookupId = cacheStore.GetContactPointTypeLookups()[0].LookupID;
        address.AvailableCountryLookupIds = cacheStore.GetCountryLookups();
        address.CountryLookupId = cacheStore.GetDefaultCountryForActiveSite().LookupID;
        address.AvailableCountryRegionLookupIds = cacheStore.GetCountryRegionLookups(cacheStore.GetDefaultCountryForActiveSite());
        address.StateLookupId = cacheStore.GetDefaultRegionForActiveSite().LookupID;
        address.ZipCode = "";

        currentAddresses.push(address);
        contactsModel = contactsModel.set("Addresses", Immutable.List(currentAddresses));
        contactsModel = contactsModel.set("ContactPoints", Immutable.List(currentContactPoints));

        return contactsModel;
    }

    private addPhoneToModel(contactsModel: IPatientContactsModel, phoneContact: IInvitationContactPhone): IPatientContactsModel {
        const currentPhones = contactsModel.get("PhoneNumbers").toArray();
        const currentContactPoints = contactsModel.get("ContactPoints").toArray();

        const contactPoint = new ContactPoint({
            Id: GuidHelper.NewGuid(),
            ContactId: contactsModel.get("PatientContactId"),
            ContactPointTypeLookupId: phoneContact.contactPointId,
            NotificationsEnabled: true
        });

        const phoneNumber = new PhoneNumber({
            AvailableCountryLookupIds: cacheStore.GetCountryLookups(),
            CountryLookupId: phoneContact.countryLookupId,
            ContactPoint_id: contactPoint.Id,
            PhoneTypeLookupId: phoneContact.phoneTypeId,
            ContactPointTypeLookupId: phoneContact.contactPointId,
            Number: phoneContact.number,
            NotificationsEnabled: true
        });

        currentContactPoints.push(contactPoint);
        currentPhones.push(phoneNumber);

        contactsModel = contactsModel
            .set("PhoneNumbers", Immutable.List(currentPhones))
            .set("ContactPoints", Immutable.List(currentContactPoints));

        return contactsModel;
    }

    private addEmptyPhoneToModel(contactsModel: IPatientContactsModel): IPatientContactsModel {
        const currentPhones = contactsModel.get("PhoneNumbers").toArray();
        const currentContactPoints = contactsModel.get("ContactPoints").toArray();

        const [newPhone, newContactPoint] = this.generateNewPhone(contactsModel.get("PatientContactId"));

        currentContactPoints.push(newContactPoint);
        currentPhones.push(newPhone);

        contactsModel = contactsModel
            .set("PhoneNumbers", Immutable.List(currentPhones))
            .set("ContactPoints", Immutable.List(currentContactPoints));

        return contactsModel;
    }

    private generateNewPhone(patientContactId: string): [PhoneNumber, ContactPoint] {
        const personalContactTypeLookUpId = cacheStore.getLookupByContactPointType(ContactPointType.Personal).LookupID;
        const phoneTypeLookUpId = cacheStore.getLookupByPhoneType(PhoneType.Mobile).LookupID;

        const contactPoint = new ContactPoint({
            Id: GuidHelper.NewGuid(),
            ContactId: patientContactId,
            ContactPointTypeLookupId: personalContactTypeLookUpId
        });

        const phoneNumber = new PhoneNumber({
            AvailableCountryLookupIds: cacheStore.GetCountryLookups(),
            CountryLookupId: cacheStore.GetDefaultCountryForActiveSite().LookupID,
            ContactPoint_id: contactPoint.Id,
            PhoneTypeLookupId: phoneTypeLookUpId,
            ContactPointTypeLookupId: personalContactTypeLookUpId,
            Number: ""
        });

        return [phoneNumber, contactPoint];
    }

    private addEmailToModel(contactsModel: IPatientContactsModel, emailContact: IInvitationContactEmail): IPatientContactsModel {
        const currentEmails = contactsModel.get("Emails").toArray();
        const currentContactPoints = contactsModel.get("ContactPoints").toArray();

        const contactPoint = new ContactPoint({
            Id: GuidHelper.NewGuid(),
            ContactId: contactsModel.get("PatientContactId"),
            ContactPointTypeLookupId: emailContact.contactPointId,
            NotificationsEnabled: true
        });

        const email = new Email({
            ContactPoint_id: contactPoint.Id,
            ContactPointTypeLookupId: emailContact.contactPointId,
            Address: emailContact.address,
            NotificationsEnabled: true
        });

        currentContactPoints.push(contactPoint);
        currentEmails.push(email);

        return contactsModel
            .set("Emails", Immutable.List(currentEmails))
            .set("ContactPoints", Immutable.List(currentContactPoints));
    }

    private addEmptyEmailToModel(contactsModel: IPatientContactsModel): IPatientContactsModel {
        const currentEmails = contactsModel.get("Emails").toArray();
        const currentContactPoints = contactsModel.get("ContactPoints").toArray();

        const [newEmail, newContactPoint] = this.generateNewEmail(contactsModel.get("PatientContactId"));

        currentContactPoints.push(newContactPoint);
        currentEmails.push(newEmail);

        return contactsModel
            .set("Emails", Immutable.List(currentEmails))
            .set("ContactPoints", Immutable.List(currentContactPoints));
    }

    private generateNewEmail(patientContactId: string): [Email, ContactPoint] {
        const personalContactTypeLookUpId = cacheStore.getLookupByContactPointType(ContactPointType.Personal).LookupID;
        const contactPoint = new ContactPoint({
            Id: GuidHelper.NewGuid(),
            ContactId: patientContactId,
            ContactPointTypeLookupId: personalContactTypeLookUpId
        });

        const email = new Email({
            ContactPoint_id: contactPoint.Id,
            ContactPointTypeLookupId: personalContactTypeLookUpId,
            Address: ""
        });

        return [email, contactPoint];
    }
}

export { PatientProfileContactsHandler };
